import { Component, Prop } from 'vue-property-decorator';
import TombolaBaseComponent from '@/components/base/base.component';
import { IGameResponseViewModel, IGameChatRoomViewModel, IGameClubViewModel, ConfigurationKey, IArcadeGameViewModel, ChatRoomConfiguration, ConfigurationPaths } from '@/app-modules/config';
import SignOutComponent from '@/components/home/_components/navigation-bar/_components/sign-out';
import { SweetAlertType } from 'sweetalert2';
import { SearchBarToggleVisibilityEventName } from '@/components/home/_components/search-bar/_domain/models';
import { IModalSpawnConfiguration } from '@/plugins/modal-manager';
import { ISweetAlertService } from '@/app-modules/sweet-alert-service';
import { container } from '@/app-modules/_ioc';
import { SERVICE_IDENTIFIERS } from '@/app-modules/_ioc/service-identifiers';
import { UserPreferences } from '@/app-modules/store/modules/user-preferences';
import { UserSettingsUpdatedEventName } from '@/components/user-settings/_domain/models';
import { ChatContextInteractionType, ChatContextInteractionSource } from '@/components/chat/_domain/modules/chat-context-menu';
import _ from 'lodash';
import { User } from '@/app-modules/store/modules/user';

@Component({
  name: 'NavigationBarComponent',
  components: {
    signOut: SignOutComponent
  }
})

class NavigationBarComponent extends TombolaBaseComponent {
  @Prop() private modalSpawnConfiguration: IModalSpawnConfiguration;
  private versionNo: string = '';
  private isPlayerNotesLiveFeatureToggleEnabled: boolean = false;
  private isUsePlayerNotesBetaLiveFeatureToggleEnabled: boolean = false;
  private playerNotesBetaList: string[] = [];

  public isPlayerNotesButtonVisible: boolean = false;

  private get sweetAlertService(): ISweetAlertService {
    return container.get(SERVICE_IDENTIFIERS.ISweetAlertService);
  }

  private get gameConfiguration(): IGameResponseViewModel[] {
    return this.modalSpawnConfiguration ? this.modalSpawnConfiguration.gameConfiguration : [];
  }

  private get gameConfigurationForWinnersMenu(): IGameResponseViewModel[] {
    return this.modalSpawnConfiguration ? this.modalSpawnConfiguration.gameConfigurationForWinners : [];
  }

  private get chatRoomConfiguration(): ChatRoomConfiguration[] {
    return this.modalSpawnConfiguration ? this.modalSpawnConfiguration.chatRoomConfiguration : [];
  }

  private get arcadeGameConfiguration(): IArcadeGameViewModel[] {
    return this.modalSpawnConfiguration ? this.modalSpawnConfiguration.arcadeGameConfiguration : [];
  }

  private beforeMount() {
    this.initialisePlayerNotesFeatureToggleState();
  }

  private mounted() {
    this.initialiseVersionNumber();
    this.setupEventBusListener();
  }

  private initialiseVersionNumber(): void {
    this.versionNo = this.configService.getConfigurationItem(ConfigurationKey.BUILD_VERSION_NO);
  }

  private initialisePlayerNotesFeatureToggleState() {
    this.configService.getConfiguration().then((config) => {
      this.isPlayerNotesLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.player_notes_islive) === 'true';
      this.isUsePlayerNotesBetaLiveFeatureToggleEnabled = config.getConfigurationByPath(ConfigurationPaths.featuretoggle.use_player_notes_beta_islive) === 'true';
      if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
        const list = config.getConfigurationByPath(ConfigurationPaths.users.playernotes_beta);
        if (list !== undefined){
          this.playerNotesBetaList = list.split(',');
        }
      }
      this.isPlayerNotesButtonVisible = this.shouldShowPlayerNotes();
    });
  }

  private spawnChatModal(game: ChatRoomConfiguration, chatRoom: IGameChatRoomViewModel): void {
    this.$modalManager.spawnAgent.spawnChatModal(game.name, chatRoom.Id, chatRoom.FrontEndName);
  }

  private spawnGameModal(game: IGameResponseViewModel, gameClub: IGameClubViewModel): void {
    this.$modalManager.spawnAgent.spawnGameModal(game.Name, gameClub.Id);
  }

  private spawnArcadeGameModal(game: IArcadeGameViewModel): void {
    this.$modalManager.spawnAgent.spawnArcadeGameModal(game.Name);
  }

  private spawnWinnersModal(game: IGameResponseViewModel): void {
    this.$modalManager.spawnAgent.spawnWinnersModal(game.Name);
  }

  private spawnSettingsModal(): void {
    this.$modalManager.spawnAgent.spawnSettingsModal();
  }

  private spawnPlayerNotesModal(): void {
    this.$modalManager.spawnAgent.spawnPlayerNotesModal();
  }

  private spawnChatContextInteractionModal(chatContextInteractionType: ChatContextInteractionType, playerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, originalChatRoomName?: string, chatContextInteractionSource?: ChatContextInteractionSource): void {
    this.$modalManager.spawnAgent.spawnChatContextInteractionModal(chatContextInteractionType, playerUsername, handleChatContextInteractionCallback, originalChatRoomName, chatContextInteractionSource);
  }

  private toggleSearchBar(): void {
    this.$eventbus.$emit(SearchBarToggleVisibilityEventName);
  }

  private togglePinNavigationMenu() {
    const userPreferences = this.$tombolaStore.Get(UserPreferences);
    userPreferences.pinNavigationMenu = !userPreferences.pinNavigationMenu;
    this.$tombolaStore.Save(userPreferences);
    this.$eventbus.$emit(UserSettingsUpdatedEventName);
  }

  private setupEventBusListener() {
    this.$eventbus.$on('spawnChatModal', this.trySpawnChatModalFromName);
    this.$eventbus.$on('spawnSettingsModal', this.trySpawnSettingsModal);
    this.$eventbus.$on('spawnPlayerNotesModal', this.trySpawnPlayerNotesModal);
    this.$eventbus.$on('spawnChatContextInteractionModal', this.tryChatContextInteractionModal);
  }

  private trySpawnChatModalFromName(name: string) {
    const chatRoom = this.$modalManager.spawnAgent.trySpawnChatModalFromRoomName(name);
    const chatRoomName = chatRoom ? chatRoom.Name : name;
    const toastOptions = this.getChatModalToastOptions(chatRoomName, !!chatRoom);
    this.sweetAlertService.show({
      title: toastOptions.title,
      text: toastOptions.text,
      type: toastOptions.type,
      toast: true,
      position: 'top-right',
      timer: 3000,
      showConfirmButton: false
    });
  }

  private getChatModalToastOptions(chatRoomName: string, success: boolean) {
    if (success) {
      return {
        title: 'Opening chatroom',
        text: `Opening the ${chatRoomName} chatroom...`,
        type: 'info' as SweetAlertType
      };
    }

    return {
      title: 'Can\'t open chatroom',
      text: `Can't open a chatroom beginning with ${chatRoomName}...`,
      type: 'error' as SweetAlertType
    };
  }

  private trySpawnSettingsModal() {
    this.$modalManager.spawnAgent.spawnSettingsModal();
  }

  private trySpawnPlayerNotesModal() {
    this.$modalManager.spawnAgent.spawnPlayerNotesModal();
  }

  private tryChatContextInteractionModal(chatContextInteractionType: ChatContextInteractionType, playerUsername: string, handleChatContextInteractionCallback: (chatContextAction: ChatContextInteractionType, actionReason?: string, actionChatRoom?: string) => void, originalChatRoomName?: string, chatContextInteractionSource?: ChatContextInteractionSource) {
    this.$modalManager.spawnAgent.spawnChatContextInteractionModal(chatContextInteractionType, playerUsername, handleChatContextInteractionCallback, originalChatRoomName, chatContextInteractionSource);
  }

  private shouldShowPlayerNotes(){
      if (!this.isPlayerNotesLiveFeatureToggleEnabled){
        return false;
      }

      if (this.isUsePlayerNotesBetaLiveFeatureToggleEnabled){
        const user = this.$tombolaStore.Get(User);
        return this.playerNotesBetaList.includes(user.username);
      }

      return true;
    }
}

export default NavigationBarComponent;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/components/home/_components/navigation-bar/navigation-bar.component.ts